import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { grupoService, membroService, supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

const CORES = ['av-red','av-gold','av-green','av-blue']
const iniciais = (nome) => nome?.split(' ').map(p=>p[0]).join('').substring(0,2).toUpperCase() || '?'

const INSTRUMENTOS_EMOJI = {
  'Violão':'🎸','Guitarra':'🎸','Baixo':'🎸','Teclado':'🎹','Piano':'🎹',
  'Bateria':'🥁','Cavaquinho':'🪕','Ukulele':'🪕','Violino':'🎻','Outro':'🎵'
}

export default function MembrosPage() {
  const { grupoId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [membros, setMembros] = useState([])
  const [loading, setLoading] = useState(true)
  const [grupo, setGrupo] = useState(null)
  const [meuPapel, setMeuPapel] = useState('membro')
  const [confirmando, setConfirmando] = useState(null) // id do membro confirmando ação
  const [showSair, setShowSair] = useState(false)
  const [saindo, setSaindo] = useState(false)

  useEffect(() => { carregar() }, [grupoId])

  async function carregar() {
    try {
      const data = await grupoService.membrosDoGrupo(grupoId)
      setMembros(data)
      const eu = data.find(m => m.user_id === user?.id)
      setMeuPapel(eu?.papel || 'membro')

      // Carrega nome do grupo
      const meus = await grupoService.meuGrupos(user.id)
      setGrupo(meus.find(g => g.id === grupoId))
    } catch (e) {
      toast.error('Erro ao carregar membros')
    } finally {
      setLoading(false)
    }
  }

  async function removerMembro(membroId, nome) {
    if (confirmando !== membroId) {
      setConfirmando(membroId)
      setTimeout(() => setConfirmando(null), 3000)
      return
    }
    try {
      await membroService.removerMembro(grupoId, membroId, user.id)
      setMembros(prev => prev.filter(m => m.user_id !== membroId))
      toast.success(`${nome} removido do grupo`)
      setConfirmando(null)
    } catch (e) {
      toast.error(e.message)
    }
  }

  async function promoverAdmin(membroId, nome) {
    try {
      await membroService.promoverAdmin(grupoId, membroId)
      setMembros(prev => prev.map(m => m.user_id === membroId ? { ...m, papel: 'admin' } : m))
      toast.success(`${nome} agora é admin!`)
    } catch (e) {
      toast.error(e.message)
    }
  }

  async function togglePodeShow(membroId, nome, valorAtual) {
    const novoValor = !valorAtual
    try {
      await membroService.togglePodeShow(grupoId, membroId, novoValor)
      setMembros(prev => prev.map(m => m.user_id === membroId ? { ...m, pode_show: novoValor } : m))
      toast.success(novoValor ? `${nome} pode iniciar shows` : `Permissão de show removida de ${nome}`)
    } catch (e) {
      toast.error(e.message)
    }
  }

  async function sairDoGrupo() {
    setSaindo(true)
    try {
      await membroService.sairDoGrupo(grupoId, user.id)
      toast.success('Você saiu do grupo')
      navigate('/grupos')
    } catch (e) {
      toast.error(e.message)
      setSaindo(false)
      setShowSair(false)
    }
  }

  const isAdmin = meuPapel === 'admin'

  return (
    <div className="app-container">
      {/* Header */}
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate(`/grupo/${grupoId}`)}
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>‹</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 18, fontWeight: 700 }}>
            👥 Membros
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            {grupo?.nome} · {membros.length} músico{membros.length !== 1 ? 's' : ''}
          </div>
        </div>
        {isAdmin && (
          <div style={{ fontSize: 11, background: 'rgba(0,207,255,0.15)', color: 'var(--gold)', border: '1px solid rgba(0,207,255,0.3)', padding: '4px 10px', borderRadius: 50, fontWeight: 700 }}>
            👑 Admin
          </div>
        )}
      </div>

      {/* Código de convite */}
      {grupo && (() => {
        const cod = grupo.codigo_convite?.toUpperCase() || ''
        // Formata: ABCD1234 → ABCD-1234  ou  ABCDEF → ABC-DEF
        const codFormatado = cod.length === 8
          ? `${cod.slice(0,4)}-${cod.slice(4)}`
          : cod.length === 6
          ? `${cod.slice(0,3)}-${cod.slice(3)}`
          : cod
        const msgWhats = encodeURIComponent(
          `🎸 Olá! Entre no grupo *${grupo.nome}* no CifrasBand!\n\nCódigo de convite: *${codFormatado}*\n\n📲 Acesse: https://cifrasband.com.br`
        )
        return (
          <div style={{ margin: '14px 20px' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Código de convite
            </div>
            {/* Código grande e legível */}
            <div style={{ background: 'rgba(0,207,255,0.06)', border: '1px solid rgba(0,207,255,0.2)', borderRadius: 14, padding: '14px 16px', marginBottom: 10, textAlign: 'center' }}>
              <div style={{ fontFamily: 'Montserrat, monospace', fontSize: 30, fontWeight: 800, letterSpacing: 6, color: '#00CFFF' }}>
                {codFormatado}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                compartilhe este código para alguém entrar no grupo
              </div>
            </div>
            {/* Botões de compartilhamento */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button onClick={() => {
                navigator.clipboard?.writeText(cod)
                toast.success('Código copiado!')
              }} style={{ padding: '11px', borderRadius: 10, border: '1px solid rgba(0,207,255,0.3)', background: 'rgba(0,207,255,0.08)', color: '#00CFFF', fontFamily: 'Montserrat,sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                📋 Copiar código
              </button>
              <a href={`https://wa.me/?text=${msgWhats}`} target="_blank" rel="noopener noreferrer"
                style={{ padding: '11px', borderRadius: 10, border: '1px solid rgba(37,211,102,0.35)', background: 'rgba(37,211,102,0.1)', color: '#25D366', fontFamily: 'Montserrat,sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Enviar no WhatsApp
              </a>
            </div>
          </div>
        )
      })()}

      {/* Lista de membros */}
      <div style={{ padding: '0 20px', flex: 1, overflowY: 'auto', paddingBottom: 100 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" style={{ width: 28, height: 28 }} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {membros.map((m, i) => {
              const p = m.profiles
              const euMesmo = m.user_id === user?.id
              const isMembroAdmin = m.papel === 'admin'
              const confirmandoEste = confirmando === m.user_id
              const temShow = !!m.pode_show

              return (
                <div key={m.id} style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${isMembroAdmin ? 'rgba(0,207,255,0.2)' : temShow ? 'rgba(255,140,0,0.2)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Avatar */}
                  <div className={`avatar av-md ${CORES[i % 4]}`}>
                    {p?.avatar_url ? <img src={p.avatar_url} alt="" /> : iniciais(p?.nome)}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p?.nome || 'Músico'}
                        {euMesmo && <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400, marginLeft: 6 }}>(você)</span>}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', gap: 8, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                      {p?.instrumento && <span>{INSTRUMENTOS_EMOJI[p.instrumento] || '🎵'} {p.instrumento}</span>}
                      {isMembroAdmin && <span style={{ color: 'var(--gold)', fontWeight: 600 }}>👑 Admin</span>}
                      {!isMembroAdmin && temShow && <span style={{ color: 'var(--brand2)', fontWeight: 600 }}>🎤 Pode show</span>}
                    </div>
                  </div>

                  {/* Ações (só admin vê, e não pode remover a si mesmo) */}
                  {isAdmin && !euMesmo && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {!isMembroAdmin && (
                          <button onClick={() => promoverAdmin(m.user_id, p?.nome)}
                            style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid rgba(0,207,255,0.3)', background: 'transparent', color: 'var(--gold)', fontSize: 11, cursor: 'pointer' }}
                            title="Promover a admin">
                            👑
                          </button>
                        )}
                        {!isMembroAdmin && (
                          <button
                            onClick={() => togglePodeShow(m.user_id, p?.nome, temShow)}
                            title={temShow ? 'Revogar permissão de show' : 'Permitir iniciar show'}
                            style={{
                              padding: '5px 8px', borderRadius: 8, fontSize: 11, cursor: 'pointer',
                              border: `1px solid ${temShow ? 'rgba(255,140,0,0.5)' : 'rgba(255,140,0,0.2)'}`,
                              background: temShow ? 'rgba(255,140,0,0.15)' : 'transparent',
                              color: temShow ? 'var(--brand2)' : 'rgba(255,140,0,0.5)',
                              fontWeight: temShow ? 700 : 400
                            }}>
                            🎤
                          </button>
                        )}
                        <button
                          onClick={() => removerMembro(m.user_id, p?.nome)}
                          style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${confirmandoEste ? 'var(--brand2)' : 'rgba(255,140,0,0.25)'}`, background: confirmandoEste ? 'rgba(255,140,0,0.15)' : 'transparent', color: confirmandoEste ? 'var(--brand2)' : 'rgba(255,140,0,0.6)', fontSize: 11, cursor: 'pointer', fontWeight: confirmandoEste ? 700 : 400, transition: 'all 0.2s' }}>
                          {confirmandoEste ? '⚠️ Confirmar' : 'Remover'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Botão sair do grupo */}
      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, padding: '12px 20px 28px', background: 'rgba(15,20,40,0.97)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <button onClick={() => setShowSair(true)}
          style={{ width: '100%', padding: 13, borderRadius: 12, border: '1px solid rgba(255,140,0,0.3)', background: 'rgba(255,140,0,0.08)', color: 'var(--brand2)', fontFamily: 'Montserrat,sans-serif', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          🚪 Sair deste grupo
        </button>
      </div>

      {/* Modal confirmação sair */}
      {showSair && (
        <div className="modal-overlay" onClick={() => setShowSair(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()} style={{ paddingBottom: 36 }}>
            <div className="modal-handle" onClick={() => setShowSair(false)} />
            <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🚪</div>
              <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
                Sair do grupo?
              </div>
              <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 }}>
                Você vai sair de <strong style={{ color: 'var(--text)' }}>{grupo?.nome}</strong>.
                Para entrar novamente, precisará do código de convite.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowSair(false)} className="btn btn-block" style={{ flex: 1 }}>
                Cancelar
              </button>
              <button onClick={sairDoGrupo} disabled={saindo}
                style={{ flex: 1, padding: 14, borderRadius: 12, border: 'none', background: 'var(--brand2)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                {saindo ? <span className="spinner" style={{ width: 18, height: 18 }} /> : 'Confirmar saída'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
